import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Loader2, Save, RotateCcw, ChevronLeft, ChevronRight, Crosshair,
  Plus, Trash2, ShieldCheck, Move
} from 'lucide-react';
import { developerTemplateAPI } from '../../lib/api';
import { toast } from 'sonner';

/**
 * PDF Template Field Editor — v2
 *  - Drag-and-drop fields on the rendered PDF page
 *  - Resize handles to adjust bounding box width/height
 *  - Edit field type, font size, options array (for checkbox/dropdown/radio/select)
 *  - Add new fields, delete fields
 *  - Mark template as human-verified (bulk-saves & flags all changed fields as manual_override via backend)
 */

const OPTION_TYPES = new Set(['checkbox', 'dropdown', 'select', 'radio']);
const FIELD_TYPES = ['text', 'date', 'textarea', 'number', 'checkbox', 'dropdown', 'select', 'radio', 'signature'];

const TestFillPositionEditor = ({ template, open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { image, page_width_pt, page_height_pt, image_width_px, image_height_px, page, total_pages }
  const [page, setPage] = useState(0);
  const [fields, setFields] = useState([]); // full editable copy of template.fields
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dirty, setDirty] = useState(false);
  const imgWrapperRef = useRef(null);
  const dragState = useRef(null); // { fieldId, mode: 'move'|'resize', startPt: {x,y}, origField }

  const loadPreview = useCallback(async (p = 0) => {
    if (!template?.id) return;
    setLoading(true);
    try {
      const res = await developerTemplateAPI.getPreview(template.id, p);
      setPreview(res.data);
      setPage(p);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [template?.id]);

  // Initialize when dialog opens
  useEffect(() => {
    if (open && template) {
      setFields((template.fields || []).map(f => ({ ...f })));
      setSelectedFieldId(null);
      setDirty(false);
      loadPreview(0);
    }
  }, [open, template, loadPreview]);

  const pageFields = useMemo(() => fields.filter(f => (f.page ?? 0) === page), [fields, page]);
  const selectedField = useMemo(() => fields.find(f => f.id === selectedFieldId), [fields, selectedFieldId]);

  const updateField = useCallback((id, patch) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
    setDirty(true);
  }, []);

  const deleteField = (id) => {
    if (!window.confirm('Delete this field?')) return;
    setFields(prev => prev.filter(f => f.id !== id));
    if (selectedFieldId === id) setSelectedFieldId(null);
    setDirty(true);
  };

  const addField = () => {
    if (!preview) return;
    const newId = `field_${Date.now()}`;
    const newField = {
      id: newId,
      name: `new_field_${fields.length + 1}`,
      label: `New Field ${fields.length + 1}`,
      field_type: 'text',
      page,
      fill_x: (preview.page_width_pt || 595) * 0.2,
      fill_y: (preview.page_height_pt || 842) * 0.2,
      fill_width: 120,
      fill_height: 14,
      font_size: 10,
      enabled: true,
      source: 'manual',
      position_source: 'manual_override',
      fill_y_is_baseline: true,
    };
    setFields(prev => [...prev, newField]);
    setSelectedFieldId(newId);
    setDirty(true);
  };

  // ---------- Drag & Resize ----------
  const imgToPdfDelta = (dx, dy) => {
    if (!preview || !imgWrapperRef.current) return { dx: 0, dy: 0 };
    const rect = imgWrapperRef.current.getBoundingClientRect();
    const scaleX = preview.page_width_pt / rect.width;
    const scaleY = preview.page_height_pt / rect.height;
    return { dx: dx * scaleX, dy: dy * scaleY };
  };

  const onPointerDown = (e, field, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFieldId(field.id);
    dragState.current = {
      fieldId: field.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      orig: {
        fill_x: field.fill_x ?? 0,
        fill_y: field.fill_y ?? 0,
        fill_width: field.fill_width ?? 120,
        fill_height: field.fill_height ?? (field.font_size ?? 10) * 1.4,
      },
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });
  };

  const onPointerMove = (e) => {
    const ds = dragState.current;
    if (!ds) return;
    const { dx, dy } = imgToPdfDelta(e.clientX - ds.startX, e.clientY - ds.startY);
    if (ds.mode === 'move') {
      updateField(ds.fieldId, {
        fill_x: Math.max(0, +(ds.orig.fill_x + dx).toFixed(1)),
        fill_y: Math.max(0, +(ds.orig.fill_y + dy).toFixed(1)),
      });
    } else if (ds.mode === 'resize') {
      updateField(ds.fieldId, {
        fill_width: Math.max(10, +(ds.orig.fill_width + dx).toFixed(1)),
        fill_height: Math.max(6, +(ds.orig.fill_height + dy).toFixed(1)),
      });
    }
  };

  const onPointerUp = () => {
    dragState.current = null;
    window.removeEventListener('pointermove', onPointerMove);
  };

  // ---------- Save ----------
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await developerTemplateAPI.updateFields(template.id, fields);
      toast.success(`Saved ${fields.length} fields`);
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save fields');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    if (dirty) {
      toast.info('Save your changes first, then mark verified.');
      return;
    }
    setVerifying(true);
    try {
      await developerTemplateAPI.verifyTemplate(template.id, true);
      toast.success('Template marked as verified ✓');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to verify');
    } finally {
      setVerifying(false);
    }
  };

  // ---------- Overlay rendering ----------
  const renderOverlays = () => {
    if (!preview) return null;
    const W = preview.page_width_pt;
    const H = preview.page_height_pt;
    return pageFields.map(f => {
      // Skip duplicates entirely — they live on top of the master field at the
      // same coordinates and would just make the canvas look messy.
      if (f.duplicate_of) return null;
      const x = f.fill_x ?? 0;
      const yBase = f.fill_y ?? 0;
      const w = f.fill_width ?? 120;
      const fontSize = f.font_size ?? 10;
      const h = f.fill_height ?? fontSize * 1.4;
      // fill_y is baseline -> box top is y - fontSize
      const topPt = yBase - fontSize;
      if (x == null || yBase == null) return null;

      const style = {
        left: `${(x / W) * 100}%`,
        top: `${(topPt / H) * 100}%`,
        width: `${(w / W) * 100}%`,
        height: `${(h / H) * 100}%`,
      };
      const isSelected = f.id === selectedFieldId;
      const isManual = f.position_source === 'manual_override';
      const isCellBounded = !!f.bounded_by_cell;
      const isClamped = !!f.overlap_clamped;
      const needsReview = !!f.needs_review;

      // Faint outline of the detected cell — shown only for the selected field
      // so admins can see exactly which table cell its value is locked into.
      const cellOutline = (isSelected && isCellBounded && f.cell_left != null) ? (
        <div
          className="absolute border border-dashed border-emerald-500/70 bg-emerald-100/10 pointer-events-none"
          style={{
            left: `${((f.cell_left - x) / W) * 100}%`,
            top: `${((f.cell_top - topPt) / H) * 100}%`,
            width: `${((f.cell_right - f.cell_left) / W) * 100}%`,
            height: `${((f.cell_bottom - f.cell_top) / H) * 100}%`,
          }}
          data-testid={`cell-outline-${f.id}`}
        />
      ) : null;

      return (
        <div
          key={f.id}
          data-testid={`field-overlay-${f.id}`}
          onPointerDown={(e) => onPointerDown(e, f, 'move')}
          className={`absolute group transition-colors ${
            isSelected
              ? 'ring-2 ring-blue-500 bg-blue-300/25 cursor-move'
              : needsReview
                ? 'ring-2 ring-orange-500/80 bg-orange-200/25 cursor-move hover:bg-orange-300/35'
                : isManual
                  ? 'ring-1 ring-purple-500/70 bg-purple-200/15 cursor-move hover:bg-purple-300/25'
                  : isClamped
                    ? 'ring-1 ring-amber-500/70 bg-amber-200/15 cursor-move hover:bg-amber-300/25'
                    : isCellBounded
                      ? 'ring-1 ring-emerald-600/80 bg-emerald-200/15 cursor-move hover:bg-emerald-300/25'
                      : 'ring-1 ring-emerald-500/70 bg-emerald-200/10 cursor-move hover:bg-emerald-300/25'
          }`}
          style={style}
          title={`${f.label || f.id} (${f.field_type})${needsReview ? ' • needs review' : ''}${isCellBounded ? ' • cell-bounded' : ''}${isClamped ? ' • width clamped' : ''}`}
        >
          {cellOutline}
          <span className="absolute -top-4 left-0 text-[9px] px-1 bg-white/90 rounded shadow-sm border text-gray-700 pointer-events-none whitespace-nowrap opacity-0 group-hover:opacity-100">
            {f.label || f.id}
            {isCellBounded && <span className="ml-1 text-emerald-700">✓ cell</span>}
            {isClamped && <span className="ml-1 text-amber-700">↔ clamped</span>}
          </span>
          {/* Resize handle (bottom-right) */}
          <div
            onPointerDown={(e) => onPointerDown(e, f, 'resize')}
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 border-blue-600 rounded-sm cursor-nwse-resize"
            style={{ pointerEvents: 'auto' }}
            data-testid={`resize-handle-${f.id}`}
          />
        </div>
      );
    });
  };

  const optionsText = (selectedField?.options || []).join('\n');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] max-h-[92vh] p-0 flex flex-col">
        <DialogHeader className="px-5 py-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Crosshair className="h-4 w-4 text-blue-600" />
            PDF Field Editor
            <span className="text-xs text-gray-400 font-normal ml-2">{template?.name}</span>
            {dirty && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Unsaved changes</Badge>}
            {template?.verified && !dirty && <Badge className="text-[10px] bg-green-600">Verified</Badge>}

            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleVerify}
                disabled={verifying || dirty}
                data-testid="verify-template-btn"
              >
                {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                Mark Verified
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAll}
                disabled={saving || !dirty}
                data-testid="save-all-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                Save all changes
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex">
          {/* LEFT — Preview */}
          <div className="flex-1 bg-gray-100 flex flex-col overflow-hidden">
            <div className="px-4 py-2 bg-white border-b flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" disabled={page <= 0 || loading} onClick={() => loadPreview(page - 1)} data-testid="preview-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-gray-500">
                Page {page + 1} {preview?.total_pages ? `of ${preview.total_pages}` : ''}
              </span>
              <Button size="sm" variant="outline" disabled={loading || (preview && page >= (preview.total_pages || 1) - 1)} onClick={() => loadPreview(page + 1)} data-testid="preview-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => loadPreview(page)} disabled={loading} data-testid="preview-refresh">
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
              <div className="ml-auto flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm ring-1 ring-emerald-500 bg-emerald-200/30" /> AI</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm ring-1 ring-emerald-600 bg-emerald-200/40" /> Cell-bounded</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm ring-1 ring-amber-500 bg-amber-200/40" /> Clamped</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm ring-2 ring-orange-500 bg-orange-200/40" /> Needs review</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm ring-1 ring-purple-500 bg-purple-200/30" /> Manual</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm ring-2 ring-blue-500 bg-blue-300/30" /> Selected</span>
                <Button size="sm" variant="outline" onClick={addField} disabled={!preview} data-testid="add-field-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add field
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-start justify-center">
              {loading && (
                <div className="flex items-center gap-2 text-gray-500 mt-20">
                  <Loader2 className="h-4 w-4 animate-spin" /> Rendering page…
                </div>
              )}
              {!loading && preview && (
                <div ref={imgWrapperRef} className="relative inline-block bg-white shadow-lg select-none" style={{ maxWidth: '100%' }}>
                  <img
                    src={`data:image/jpeg;base64,${preview.image}`}
                    alt="PDF preview"
                    className="block max-w-full h-auto select-none pointer-events-none"
                    draggable={false}
                    data-testid="preview-image"
                  />
                  {renderOverlays()}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Field list + editor */}
          <div className="w-[400px] border-l bg-white flex flex-col shrink-0 overflow-hidden">
            {!selectedField && (
              <>
                <div className="px-4 py-3 border-b shrink-0">
                  <h3 className="text-sm font-semibold">Fields on Page {page + 1} ({pageFields.length})</h3>
                  {(() => {
                    // V4.9 health summary — surfaces the AI-detection clamping results
                    // so admins can see at a glance how many fields are auto-constrained
                    // to their table cell vs how many had to be overlap-shrunk.
                    const visible = pageFields.filter(f => !f.duplicate_of);
                    const bounded = visible.filter(f => f.bounded_by_cell).length;
                    const clamped = visible.filter(f => f.overlap_clamped).length;
                    const manual = pageFields.filter(f => f.position_source === 'manual_override').length;
                    const duplicates = pageFields.length - visible.length;
                    const needsReview = visible.filter(f => f.needs_review && f.position_source !== 'manual_override').length;
                    return (
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]" data-testid="field-health-summary">
                        {needsReview > 0 && (
                          <Badge variant="outline" className="text-orange-700 border-orange-300 bg-orange-50" title="AI auto-detection was uncertain for these fields — please verify their position">
                            🟠 {needsReview} needs review
                          </Badge>
                        )}
                        {bounded > 0 && (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50" title="Stamped values are constrained to their detected table cell">
                            ✓ {bounded} cell-bounded
                          </Badge>
                        )}
                        {clamped > 0 && (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50" title="Width auto-shrunk to avoid overlapping the next field on the row">
                            ↔ {clamped} clamped
                          </Badge>
                        )}
                        {manual > 0 && (
                          <Badge variant="outline" className="text-purple-700 border-purple-300 bg-purple-50">
                            ✎ {manual} manual
                          </Badge>
                        )}
                        {duplicates > 0 && (
                          <Badge variant="outline" className="text-slate-600 border-slate-300 bg-slate-50" title="Duplicate AI detections collapsed — they would have stamped on top of each other">
                            {duplicates} duplicate{duplicates === 1 ? '' : 's'}
                          </Badge>
                        )}
                      </div>
                    );
                  })()}
                  <p className="text-[11px] text-gray-500 mt-2">
                    <Move className="h-3 w-3 inline mr-1" />
                    Drag any field box to reposition. Drag the blue corner to resize. Click a field to edit options & type.
                  </p>
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-2 space-y-1">
                    {pageFields.length === 0 && (
                      <p className="text-xs text-gray-400 p-4 text-center">No fields on this page. Click "Add field" to add one.</p>
                    )}
                    {pageFields.map(f => {
                      const isMissing = f.fill_x == null || f.fill_y == null;
                      const isManual = f.position_source === 'manual_override';
                      const isDuplicate = !!f.duplicate_of;
                      const isCellBounded = !!f.bounded_by_cell;
                      const isClamped = !!f.overlap_clamped;
                      const needsReview = !!f.needs_review;
                      return (
                        <button
                          key={f.id}
                          onClick={() => setSelectedFieldId(f.id)}
                          className={`w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 flex items-center gap-1.5 text-xs ${isMissing || isDuplicate ? 'opacity-60' : ''}`}
                          data-testid={`field-row-${f.id}`}
                        >
                          <div className="flex-1 truncate">
                            <p className="font-medium truncate">{f.label || f.id}</p>
                            <p className="text-[10px] text-gray-400">
                              {f.field_type}
                              {f.fill_x != null && f.fill_y != null && ` • (${Math.round(f.fill_x)}, ${Math.round(f.fill_y)})`}
                              {OPTION_TYPES.has(f.field_type) && ` • ${(f.options || []).length} options`}
                            </p>
                          </div>
                          {needsReview && !isManual && (
                            <Badge variant="outline" className="text-[9px] text-orange-700 border-orange-300 bg-orange-50" title={f.low_confidence_reason || 'AI auto-detection was uncertain — please verify'} data-testid={`badge-needs-review-${f.id}`}>
                              🟠 review
                            </Badge>
                          )}
                          {isManual && <Badge variant="outline" className="text-[9px] text-purple-600 border-purple-300" data-testid={`badge-manual-${f.id}`}>manual</Badge>}
                          {!isManual && !needsReview && isCellBounded && (
                            <Badge variant="outline" className="text-[9px] text-emerald-700 border-emerald-300 bg-emerald-50" title="Cell-bounded: stamped value is constrained to its table cell" data-testid={`badge-cell-bounded-${f.id}`}>
                              ✓ cell
                            </Badge>
                          )}
                          {!isManual && isClamped && (
                            <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300 bg-amber-50" title="Width auto-shrunk so it no longer overlaps the next field on this row" data-testid={`badge-clamped-${f.id}`}>
                              clamped
                            </Badge>
                          )}
                          {isDuplicate && (
                            <Badge variant="outline" className="text-[9px] text-slate-600 border-slate-300" title={`Duplicate of ${f.duplicate_of} — not rendered to avoid stacking text on top of itself`} data-testid={`badge-duplicate-${f.id}`}>
                              dup
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </>
            )}

            {selectedField && (
              <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setSelectedFieldId(null)}>
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to list
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => deleteField(selectedField.id)} data-testid="delete-field-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </div>

                {/* V4.9 — Position status panel: shows the detection / clamping
                    outcome for the currently-selected field so admins can spot
                    cell-bounded vs auto-clamped vs manual-override at a glance. */}
                {(selectedField.bounded_by_cell || selectedField.overlap_clamped || selectedField.duplicate_of || selectedField.needs_review || selectedField.position_source === 'manual_override') && (
                  <div className="px-4 py-2 border-b bg-gray-50 space-y-1 text-[11px]" data-testid="field-position-status">
                    {selectedField.needs_review && (
                      <div className="text-orange-700">
                        🟠 <strong>Needs review</strong>{selectedField.label_score != null && ` (label score ${selectedField.label_score}/10)`} — {selectedField.low_confidence_reason || 'AI was uncertain. Drag the box to the correct cell and click Save.'}
                      </div>
                    )}
                    {selectedField.recovered_from_sibling && (
                      <div className="text-orange-600">
                        Recovered from sibling field <code className="bg-white px-1 rounded">{selectedField.recovered_from_sibling}</code> — position is a best-guess.
                      </div>
                    )}
                    {selectedField.position_source === 'manual_override' && (
                      <div className="text-purple-700">✎ Manually positioned — preserved across future auto-detection runs.</div>
                    )}
                    {selectedField.bounded_by_cell && (
                      <div className="text-emerald-700" title={`Cell: (${selectedField.cell_left}, ${selectedField.cell_top}) – (${selectedField.cell_right}, ${selectedField.cell_bottom})`}>
                        ✓ Cell-bounded — value can't bleed past x={Math.round(selectedField.cell_right)}.
                      </div>
                    )}
                    {selectedField.overlap_clamped && (
                      <div className="text-amber-700">
                        ↔ Width auto-shrunk to avoid the field on its right. Override here if it's too narrow.
                      </div>
                    )}
                    {selectedField.duplicate_of && (
                      <div className="text-slate-600">
                        Duplicate of <code className="bg-white px-1 rounded">{selectedField.duplicate_of}</code> — not rendered. Drag it to a unique spot to enable it.
                      </div>
                    )}
                  </div>
                )}

                <div className="p-4 space-y-3">
                  <div>
                    <Label className="text-xs text-gray-500">Label</Label>
                    <Input
                      value={selectedField.label || ''}
                      onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                      data-testid="edit-label"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-500">Field type</Label>
                      <Select
                        value={selectedField.field_type || 'text'}
                        onValueChange={(v) => updateField(selectedField.id, { field_type: v })}
                      >
                        <SelectTrigger data-testid="edit-field-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FIELD_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Name (mapping key)</Label>
                      <Input
                        value={selectedField.name || ''}
                        onChange={(e) => updateField(selectedField.id, { name: e.target.value })}
                        data-testid="edit-name"
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <Label className="text-[11px] text-gray-500 uppercase tracking-wide">Position (PDF points)</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div>
                        <Label className="text-[11px] text-gray-500">X</Label>
                        <Input type="number" step="0.5"
                          value={selectedField.fill_x ?? ''}
                          onChange={(e) => updateField(selectedField.id, { fill_x: e.target.value === '' ? null : parseFloat(e.target.value) })}
                          data-testid="edit-fill-x" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Y (baseline)</Label>
                        <Input type="number" step="0.5"
                          value={selectedField.fill_y ?? ''}
                          onChange={(e) => updateField(selectedField.id, { fill_y: e.target.value === '' ? null : parseFloat(e.target.value) })}
                          data-testid="edit-fill-y" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Width</Label>
                        <Input type="number" step="1"
                          value={selectedField.fill_width ?? ''}
                          onChange={(e) => updateField(selectedField.id, { fill_width: e.target.value === '' ? null : parseFloat(e.target.value) })}
                          data-testid="edit-fill-width" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Height</Label>
                        <Input type="number" step="1"
                          value={selectedField.fill_height ?? ''}
                          onChange={(e) => updateField(selectedField.id, { fill_height: e.target.value === '' ? null : parseFloat(e.target.value) })}
                          data-testid="edit-fill-height" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Font size</Label>
                        <Input type="number" step="1"
                          value={selectedField.font_size ?? 10}
                          onChange={(e) => updateField(selectedField.id, { font_size: parseFloat(e.target.value) || 10 })}
                          data-testid="edit-font-size" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Page</Label>
                        <Input type="number" step="1" min="0"
                          value={selectedField.page ?? 0}
                          onChange={(e) => updateField(selectedField.id, { page: parseInt(e.target.value) || 0 })}
                          data-testid="edit-page" />
                      </div>
                    </div>
                  </div>

                  {OPTION_TYPES.has(selectedField.field_type) && (
                    <div className="pt-2 border-t">
                      <Label className="text-[11px] text-gray-500 uppercase tracking-wide">
                        Options (one per line)
                      </Label>
                      <Textarea
                        rows={6}
                        className="mt-1 text-xs font-mono"
                        placeholder={"Bumiputera\nNon-Bumiputera\nForeigner"}
                        value={optionsText}
                        onChange={(e) => updateField(selectedField.id, {
                          options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                        })}
                        data-testid="edit-options"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">
                        {(selectedField.options || []).length} option(s). Used by mobile + PDF rendering for {selectedField.field_type} fields.
                      </p>
                    </div>
                  )}

                  <div className="rounded-md bg-blue-50 border border-blue-100 p-2 text-[11px] text-blue-700">
                    <Move className="h-3 w-3 inline mr-1" />
                    Tip: you can also drag the highlighted box on the preview, or drag the blue corner to resize.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TestFillPositionEditor;
